import { FC, useRef } from 'react';
import { Flex } from 'antd';
import dayjs from 'dayjs';
import {
  SDropdown,
  ErrorMessage,
  SLabelTitle,
  SMailSelector,
  ModalDialog,
  StatusDropdown,
  UploadButton,
  SInput,
  STextArea,
  SDatePicker,
} from '../..';
import { IProps } from './type';
import globalStyles from '../../../assets/styles/index.module.scss';
import { useAppSelector } from '../../../store';
import { DATE_FORMAT } from '../../../assets/conts';
import { modalTypes } from '../../../assets/enums';
import { useSetOrganizationOnOpen } from '../../../hooks';

const ExpeditionCertificateModal: FC<IProps> = (props) => {
  const { getFieldProps, setFieldValue, values, errors, touched } =
    props.formik;
  const uploadInput = useRef<HTMLInputElement | null>(null);
  const { organization } = useAppSelector(
    (state) => state.userReducer.userInfo,
  );

  useSetOrganizationOnOpen(props.open, values, setFieldValue);

  return (
    <ModalDialog
      title={props.title}
      subTitle={props.subTitle}
      open={props.open}
      handleOk={props.handleSubmit}
      handleCancel={props.handleCancel}
    >
      <Flex gap="middle" vertical>
        <Flex align="flex-start" justify="space-between">
          <SLabelTitle>Şirket Bilgileri</SLabelTitle>
          <Flex vertical gap="small">
            <SDropdown
              id="organization"
              value={
                props.organizations.find(
                  (item) =>
                    item.id ===
                    (values.organization ||
                      (organization !== 1 ? organization : undefined)),
                )?.name
              }
              placeholder="Şirket Seçiniz"
              className={globalStyles.width200px}
              disabled={organization !== 1}
              options={props.organizations.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
              onChange={(value) => setFieldValue('organization', value)}
            />
            {touched.organization && errors.organization && (
              <ErrorMessage>{errors.organization}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        <Flex gap="small" vertical>
          <SLabelTitle>Emtia Bilgileri</SLabelTitle>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SInput
                placeholder="Emtia Cinsi"
                {...getFieldProps('goods_type')}
              />
              {touched.goods_type && errors.goods_type && (
                <ErrorMessage>{errors.goods_type}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SInput
                type="number"
                placeholder="Emtia Miktarı"
                {...getFieldProps('goods_quantity')}
              />
              {touched.goods_quantity && errors.goods_quantity && (
                <ErrorMessage>{errors.goods_quantity}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneThird}>
              <SDropdown
                id="goods_unit"
                value={values.goods_unit}
                placeholder="Emtia Birimi"
                options={props.unitOfGoods.map((uog) => ({
                  value: uog.key,
                  label: uog.value,
                }))}
                onChange={(value) => setFieldValue('goods_unit', value)}
              />
              {touched.goods_unit && errors.goods_unit && (
                <ErrorMessage>{errors.goods_unit}</ErrorMessage>
              )}
            </Flex>
          </Flex>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SInput
                type="number"
                placeholder="Emtia Bedeli"
                {...getFieldProps('insurance_value')}
              />
              {touched.insurance_value && errors.insurance_value && (
                <ErrorMessage>{errors.insurance_value}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDropdown
                id="insurance_value_unit"
                value={values.insurance_value_unit}
                placeholder="Para Birimi"
                options={props.currencies.map((c) => ({
                  value: c.key,
                  label: c.value,
                }))}
                onChange={(value) =>
                  setFieldValue('insurance_value_unit', value)
                }
              />
              {touched.insurance_value_unit && errors.insurance_value_unit && (
                <ErrorMessage>{errors.insurance_value_unit}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SDatePicker
                id="invoice_date"
                name="invoice_date"
                value={
                  values.invoice_date
                    ? dayjs(values.invoice_date, DATE_FORMAT)
                    : null
                }
                placeholder="Fatura Tarihi"
                onChange={(date) => {
                  if (date) {
                    setFieldValue('invoice_date', date.format(DATE_FORMAT));
                  }
                }}
              />
              {touched.invoice_date && errors.invoice_date && (
                <ErrorMessage>{errors.invoice_date}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthOneQuarter}>
              <SInput
                placeholder="Fatura No"
                {...getFieldProps('invoice_number')}
              />
              {touched.invoice_number && errors.invoice_number && (
                <ErrorMessage>{errors.invoice_number}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        {props.type === modalTypes.EDIT && (
          <Flex gap="small" vertical>
            <SLabelTitle>Poliçe Bilgileri</SLabelTitle>
            <Flex justify="space-between" gap="small">
              <Flex vertical gap="small" className={globalStyles.widthOneThird}>
                <SInput
                  type="number"
                  placeholder="Poliçe Değeri"
                  {...getFieldProps('policy_value')}
                />
                {touched.policy_value && errors.policy_value && (
                  <ErrorMessage>{errors.policy_value}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={globalStyles.widthOneThird}>
                <SDropdown
                  id="policy_value_unit"
                  value={values.policy_value_unit}
                  placeholder="Değer Birimi"
                  options={props.currencies.map((c) => ({
                    value: c.key,
                    label: c.value,
                  }))}
                  onChange={(value) =>
                    setFieldValue('policy_value_unit', value)
                  }
                />
                {touched.policy_value_unit && errors.policy_value_unit && (
                  <ErrorMessage>{errors.policy_value_unit}</ErrorMessage>
                )}
              </Flex>
              <Flex vertical gap="small" className={globalStyles.widthOneThird}>
                <SInput
                  placeholder="Poliçe No"
                  {...getFieldProps('policy_number')}
                />
                {touched.policy_number && errors.policy_number && (
                  <ErrorMessage>{errors.policy_number}</ErrorMessage>
                )}
              </Flex>
            </Flex>
          </Flex>
        )}
        <Flex vertical gap="small">
          <SLabelTitle>Sevkiyat Bilgileri</SLabelTitle>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDropdown
                id="transportation_type"
                value={values.transportation_type}
                placeholder="Sevkiyat Tipi"
                options={props.transportationTypes.map((tt) => ({
                  value: tt.key,
                  label: tt.value,
                }))}
                onChange={(value) =>
                  setFieldValue('transportation_type', value)
                }
              />
              {touched.transportation_type && errors.transportation_type && (
                <ErrorMessage>{errors.transportation_type}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDatePicker
                id="expedition_date"
                name="expedition_date"
                value={
                  values.expedition_date
                    ? dayjs(values.expedition_date, DATE_FORMAT)
                    : null
                }
                placeholder="Sevk Tarihi"
                onChange={(date) => {
                  if (date) {
                    setFieldValue('expedition_date', date.format(DATE_FORMAT));
                  }
                }}
              />
              {touched.expedition_date && errors.expedition_date && (
                <ErrorMessage>{errors.expedition_date}</ErrorMessage>
              )}
            </Flex>
          </Flex>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput
                placeholder="Çıkış Ülkesi"
                {...getFieldProps('origin_country')}
              />
              {touched.origin_country && errors.origin_country && (
                <ErrorMessage>{errors.origin_country}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput
                placeholder="Varış Ülkesi"
                {...getFieldProps('destination_country')}
              />
              {touched.destination_country && errors.destination_country && (
                <ErrorMessage>{errors.destination_country}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        {props.type === modalTypes.EDIT && (
          <UploadButton
            fileName={[...values.upload_files]?.map((item) => item.name)}
            uploadRef={uploadInput}
            onChange={props.handleUploadFile}
          />
        )}
        <Flex vertical gap="small">
          <SLabelTitle>Taşıt Bilgileri</SLabelTitle>
          <Flex justify="space-between" gap="small">
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SDropdown
                id="vehicle_type"
                value={values.vehicle_type}
                placeholder="Taşıt Cinsi"
                options={props.vehicleTypes.map((vt) => ({
                  value: vt.key,
                  label: vt.value,
                }))}
                onChange={(value) => setFieldValue('vehicle_type', value)}
              />
              {touched.vehicle_type && errors.vehicle_type && (
                <ErrorMessage>{errors.vehicle_type}</ErrorMessage>
              )}
            </Flex>
            <Flex vertical gap="small" className={globalStyles.widthHalf}>
              <SInput
                placeholder="Taşıt Bilgisi"
                {...getFieldProps('vehicle_info')}
              />
              {touched.vehicle_info && errors.vehicle_info && (
                <ErrorMessage>{errors.vehicle_info}</ErrorMessage>
              )}
            </Flex>
          </Flex>
        </Flex>
        {props.type === modalTypes.EDIT && organization === 1 && (
          <Flex justify="space-between">
            <SLabelTitle>Durum Bilgisi</SLabelTitle>
            <StatusDropdown
              status={values.recent_status}
              statusChoices={props.statusChoices}
              onChange={(value) => setFieldValue('recent_status', value)}
            />
          </Flex>
        )}
        <Flex vertical gap="small">
          <SLabelTitle>Açıklama</SLabelTitle>
          <Flex vertical gap="small">
            <STextArea
              placeholder="Açıklama"
              {...getFieldProps('description')}
            />
            {touched.description && errors.description && (
              <ErrorMessage>{errors.description}</ErrorMessage>
            )}
          </Flex>
        </Flex>
        {props.type === modalTypes.EDIT && (
          <SMailSelector
            value={values.email_users}
            placeholder="Mail Listesi"
            options={props.withViewPerm?.map((wvp) => ({
              value: wvp.value,
              label: wvp.label,
            }))}
            onChange={(value) => setFieldValue('email_users', value)}
          />
        )}
      </Flex>
    </ModalDialog>
  );
};

export default ExpeditionCertificateModal;
